'use client';

import {
  DropdownTriggerArrowCloseIconButton,
  DropdownTriggerArrowOpenIconButton,
  DropdownTriggerBox,
  DropdownTriggerInput,
  StaticDropdownMenu,
  StaticDropdownMenuContent,
  StaticDropdownMenuItem,
  StaticDropdownMenuTrigger,
  Typography,
} from '@musinsa/mds';
import { useState } from 'react';
import { twMerge } from 'tailwind-merge';
import type { ProductOptionItem } from '../types';

interface OptionDropdownProps {
  /** placeholder — '색상' / '사이즈' */
  placeholder: string;
  value: string | null;
  options: ProductOptionItem[];
  onChange: (next: string) => void;
  /** 트리거 선택값 커스텀 렌더 — 컬러칩 swatch 등. 미지정 시 option.label */
  renderTriggerValue?: (option: ProductOptionItem) => React.ReactNode;
  /** 옵션 라벨 커스텀 렌더 — 미지정 시 label */
  renderOptionLabel?: (option: ProductOptionItem) => React.ReactNode;
  /** 마운트 시 자동 open (mock 검수용) */
  defaultOpen?: boolean;
  disabled?: boolean;
  className?: string;
}

/**
 * 옵션 시트 안에서 사용되는 dropdown 공통 wrapper.
 *
 * ── 동작 (mds `StaticDropdownMenu` 패밀리 기반) ──
 * - 트리거 클릭 시 open 상태가 토글된다. 외부에서 disabled 가 true 면 토글을 무시한다.
 * - 옵션 클릭 시 각 Item 에 prop 으로 넘긴 value 가 mds context 를 통해
 *   onValueChange 로 전달되고, 컨슈머의 onChange 가 호출됨과 동시에
 *   onOpenChange(false) 가 자동으로 같이 발생해 패널이 닫힌다.
 *   → Item 에 별도 onClick 핸들러를 둘 필요 없음 (value prop 만 전달하면 끝).
 * - 외부 영역을 클릭하면 닫는 동작은 mds 내부의 useOnClickOutside 가 처리하므로
 *   컨슈머에서 별도 핸들러를 작성할 필요 없음.
 * - 콘텐츠 패널이 portal/popover 가 아니라 트리거의 sibling 으로 렌더되는
 *   "인라인 expansion" 이라 시트 내부 콘텐츠를 아래로 밀어내며 시트 스크롤
 *   영역에 그대로 통합된다 (Radix DropdownMenu 와의 핵심 차이).
 *
 * ── 품절 옵션 처리 ──
 * - disabled={option.soldOut} 만 넘기면 mds 가 마우스/키보드 인터랙션을 모두 차단한다.
 * - 시각적으로는 옅은 회색 배경 + 중간 회색 텍스트 + 라벨 끝에 공백 없이 "(품절)" 접미.
 *   (Figma 사양: "S(품절)" 형태)
 *
 * ── 일반 옵션의 색상을 override 하는 이유 ──
 * - mds Item 의 기본 스타일은 두 가지를 깔아둔다.
 *   (a) 텍스트를 중간 회색으로 적용.
 *   (b) data-disabled 속성이 "존재하기만" 하면 옅은 회색 배경을 적용 (값 무관).
 * - React 는 disabled boolean 이 false 일 때도 attribute 로 "false" 문자열을
 *   stringify 하기 때문에 일반 옵션 행도 (b) 셀렉터에 걸려 회색 배경이 박힌다.
 * - 따라서 컨슈머 쪽에서 일반 옵션 행에 흰색 배경 + 검정 텍스트를 important 로
 *   강제 override 하고, 품절 옵션 행은 의도된 옅은 회색 배경 + 중간 회색 텍스트를
 *   동일하게 important 로 다시 명시한다.
 */
export const OptionDropdown = ({
  placeholder,
  value,
  options,
  onChange,
  renderTriggerValue,
  renderOptionLabel,
  defaultOpen,
  disabled,
  className,
}: OptionDropdownProps) => {
  const [open, setOpen] = useState(defaultOpen ?? false);
  const selectedOption = value ? (options.find((opt) => opt.code === value) ?? null) : null;

  return (
    <StaticDropdownMenu
      open={open}
      onOpenChange={(next) => !disabled && setOpen(next)}
      onValueChange={(next) => {
        onChange(next as string);
      }}
      className={twMerge('w-full', className)}
    >
      {/* mds 는 trigger(group)의 `data-disabled` 존재만 보고 input 에 group-data-[disabled] 비활성
          스타일(회색 bg/placeholder/not-allowed)을 적용한다. React 가 data 속성을 `false` 도 문자열로
          stringify 해 disabled=false 면 `data-disabled="false"` 가 "존재"하므로 비활성처럼 렌더된다.
          → false 를 undefined 로 바꿔 속성 자체를 제거한다 (repo 공통 `cond ? true : undefined` 패턴). */}
      <StaticDropdownMenuTrigger asChild disabled={disabled || undefined}>
        <DropdownTriggerBox className="relative w-full cursor-pointer">
          <DropdownTriggerInput
            readOnly
            // renderTriggerValue(컬러 swatch+라벨 오버레이)가 선택값을 그리는 경우 input 의
            // value/placeholder 를 모두 비운다. 비우지 않으면 input 라벨/placeholder 가 오버레이와
            // 겹쳐 "블랙 블랙"(value 중복) 또는 "컬러" placeholder 가 비쳐 보인다.
            value={selectedOption && !renderTriggerValue ? selectedOption.label : ''}
            placeholder={selectedOption && renderTriggerValue ? '' : placeholder}
            disabled={disabled || undefined}
            fullWidth
            // iOS Safari 가 readOnly 와 무관하게 input tap 시 focus → scrollIntoView
            // 를 자동 실행해 배경 페이지가 한 번 점프하는 문제 차단. body overflow:hidden
            // 으로도 막을 수 없어 input 자체가 터치를 받지 않게 해야 함.
            // (community-frontend PR #4848 / FE-24036 동일 패턴)
            tabIndex={-1}
            style={{ pointerEvents: 'none' }}
            className="cursor-pointer"
          />
          {selectedOption && renderTriggerValue && (
            <div className="pointer-events-none absolute left-[12px] top-0 flex h-full items-center">
              {renderTriggerValue(selectedOption)}
            </div>
          )}
          {open ? <DropdownTriggerArrowOpenIconButton /> : <DropdownTriggerArrowCloseIconButton />}
        </DropdownTriggerBox>
      </StaticDropdownMenuTrigger>
      <StaticDropdownMenuContent className="w-full bg-white">
        {options.map((option) => (
          <StaticDropdownMenuItem
            key={option.code}
            value={option.code}
            disabled={option.soldOut}
            className={twMerge(
              // mds Item 기본 스타일이 텍스트를 중간 회색으로 깔고, data-disabled
              // 속성 존재만 보고 옅은 회색 배경을 적용한다 (값 무관). React 가
              // disabled=false 도 attribute 로 stringify 하므로 일반 옵션도 회색
              // 배경이 박힌다. 그래서 일반 행은 흰색 배경 + 검정 텍스트를 important
              // 로 덮어쓰고, 품절 행은 의도된 옅은 회색 배경 + 중간 회색 텍스트를
              // 다시 important 로 명시한다. (자세한 내막은 파일 상단 JSDoc 참고)
              // 목록 항목 height 40px, 텍스트 13px/#666666(gray-600). (SID-4429)
              'flex items-center gap-[8px] px-[16px]! h-[40px]!',
              option.soldOut
                ? 'cursor-not-allowed bg-gray-100! text-gray-500'
                : 'bg-white! text-gray-600! pointer-events-auto!',
            )}
          >
            {renderOptionLabel ? (
              renderOptionLabel(option)
            ) : (
              <Typography
                variant="body13pxReg"
                colorScheme={option.soldOut ? 'gray-500' : 'gray-600'}
              >
                {option.label}
                {/* Figma 사양: 라벨과 "(품절)" 사이 공백 없음 — "S(품절)" 형태. */}
                {option.soldOut && '(품절)'}
              </Typography>
            )}
          </StaticDropdownMenuItem>
        ))}
      </StaticDropdownMenuContent>
    </StaticDropdownMenu>
  );
};
